import React, { createContext, useState, useCallback, useEffect, useRef } from 'react'
import { SYNC_CONFIG } from '../services/syncConfig'
import googleDriveAuthService from '../services/cloud/googleDriveAuthService'
import cloudConnectionService from '../services/cloud/cloudConnectionService'
import cloudTokenService from '../services/cloud/cloudTokenService'
import cloudFileService from '../services/cloud/cloudFileService'
import cloudReconnectionService from '../services/cloud/cloudReconnectionService'
import cloudEventHandlersService from '../services/cloud/cloudEventHandlersService'
import cloudConnectionManagementService from '../services/cloud/cloudConnectionManagementService'
import cloudOAuthService from '../services/cloud/cloudOAuthService'
import cloudServerOperationsService from '../services/cloud/cloudServerOperationsService'
import cloudInitializationService from '../services/cloud/cloudInitializationService'

/**
 * Cloud Storage Context for managing cloud provider authentication and operations.
 * 
 * Provides unified interface for Google Drive, Dropbox, OneDrive, etc.
 */

const CloudStorageContext = createContext({
  isConnected: false,
  connectedProvider: null,
  accessToken: null,
  refreshToken: null,
  userInfo: null,
  loading: false,
  authenticateWithGoogleDrive: () => Promise.resolve({ success: false, error: 'Not initialized' }),
  disconnectProvider: () => Promise.resolve({ success: false, error: 'Not initialized' }),
  testGoogleDriveConnection: () => Promise.resolve({ success: false, error: 'Not initialized' }),
  clearCloudTokens: () => {},
  refreshAccessToken: () => Promise.resolve(null),
  resetReconnectionState: () => {},
  stopReconnectionAttempts: () => {},
  uploadFile: () => Promise.reject(new Error('Not initialized')),
  downloadFile: () => Promise.reject(new Error('Not initialized')),
  deleteFile: () => Promise.reject(new Error('Not initialized')),
  listFiles: () => Promise.reject(new Error('Not initialized')),
  createFolder: () => Promise.reject(new Error('Not initialized')),
  getCloudStorageSettings: () => Promise.reject(new Error('Not initialized')),
  updateCloudStorageSettings: () => Promise.reject(new Error('Not initialized')),
  getStorageQuota: () => Promise.resolve({ success: false, error: 'Not initialized' })
})

export { CloudStorageContext }

export const CloudStorageProvider = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false)
  const [connectedProvider, setConnectedProvider] = useState(null)
  const [accessToken, setAccessToken] = useState(null)
  const [refreshToken, setRefreshToken] = useState(null)
  const [userInfo, setUserInfo] = useState(null)
  const [loading, setLoading] = useState(false)

  // Loop prevention
  const connectionTestAttemptedRef = useRef(false)

  // Set state functions object for services
  const setStateFunctions = {
    setIsConnected,
    setConnectedProvider,
    setAccessToken,
    setRefreshToken,
    setUserInfo,
    setLoading
  }

  /**
   * Initialize cloud storage context
   */
  useEffect(() => {
    if (!cloudInitializationService.shouldInitialize()) {
      return
    }
    
    const initializeCloudStorage = async () => {
      await cloudInitializationService.initializeCloudStorage(setStateFunctions, {
        testGoogleDriveConnection,
        clearCloudTokens,
        verifyCloudConnectionWithBackend,
        setupReconnectionTimer
      })
    }
    
    initializeCloudStorage()
    
    // Create context object for services
    const contextForServices = {
      isConnected,
      loading,
      connectionTestAttemptedRef,
      testGoogleDriveConnection,
      scheduleReconnectAttempt
    }
    
    // Set up event handlers and token monitoring
    cloudEventHandlersService.setupEventHandlers(contextForServices, setStateFunctions)
    const cleanupTokenMonitoring = cloudEventHandlersService.setupTokenMonitoring(contextForServices, setStateFunctions)
    
    return () => {
      cloudEventHandlersService.cleanup()
      cleanupTokenMonitoring?.()
      cloudReconnectionService.cleanup()
    }
  }, [isConnected, loading]) // Include dependencies that services need

  /**
   * Listen for cloud tokens refreshed event
   */
  useEffect(() => {
    const handleCloudTokensRefreshed = (event) => {
      console.log('🔄 CloudStorageContext: Received cloudTokensRefreshed event:', event.detail)
      const { accessToken: eventAccessToken, refreshToken: eventRefreshToken, userInfo: eventUserInfo, connected } = event.detail
      
      if (eventAccessToken) {
        console.log('✅ CloudStorageContext: Updating state with refreshed cloud tokens')
        setAccessToken(eventAccessToken)
        setRefreshToken(eventRefreshToken)
        setUserInfo(eventUserInfo)
        setIsConnected(connected || true)
        setConnectedProvider('google_drive')
        
        // Store tokens using cloudTokenService
        cloudTokenService.storeTokens(eventAccessToken, eventRefreshToken, eventUserInfo)
        
        // Dispatch accessTokenReady event for other services
        window.dispatchEvent(new CustomEvent('accessTokenReady', {
          detail: {
            accessToken: eventAccessToken,
            userInfo: eventUserInfo
          }
        }))
        
        console.log('✅ CloudStorageContext: Cloud tokens refreshed and state updated')
      }
    }

    window.addEventListener('cloudTokensRefreshed', handleCloudTokensRefreshed)
    
    return () => {
      window.removeEventListener('cloudTokensRefreshed', handleCloudTokensRefreshed)
    }
  }, [])

  /**
   * Verify cloud connection status with backend
   */
  const verifyCloudConnectionWithBackend = async (authToken) => {
    return await cloudConnectionManagementService.verifyCloudConnectionWithBackend(authToken, setStateFunctions)
  }

  /**
   * Clear connection data and state
   */
  const clearConnectionData = (preserveRefreshToken = false) => {
    cloudConnectionManagementService.clearConnectionData(setStateFunctions, preserveRefreshToken)
  }

  /**
   * Authenticate with Google Drive
   */
  const authenticateWithGoogleDrive = async () => {
    try {
      setLoading(true)
      console.log('🚀 CloudStorageContext: Starting Google Drive authentication...')
      
      const result = await googleDriveAuthService.authenticate()
      
      if (result.success) {
        console.log('✅ CloudStorageContext: Google Drive authentication successful')
        setIsConnected(true)
        setConnectedProvider('google_drive')
        setAccessToken(result.accessToken)
        setRefreshToken(result.refreshToken)
        setUserInfo(result.userInfo)
        
        // Store tokens
        cloudTokenService.storeTokens(result.accessToken, result.refreshToken, result.userInfo)
        
        return { success: true, data: result }
      } else {
        console.log('❌ CloudStorageContext: Google Drive authentication failed:', result.error)
        return { success: false, error: result.error }
      }
    } catch (error) {
      console.error('❌ CloudStorageContext: Google Drive authentication error:', error)
      return { success: false, error: error.message }
    } finally {
      setLoading(false)
    }
  }

  /**
   * Disconnect from cloud provider
   */
  const disconnectProvider = async (provider) => {
    try {
      console.log(`🔌 CloudStorageContext: Disconnecting from ${provider}...`)
      
      if (provider === 'google_drive') {
        await googleDriveAuthService.disconnect()
      }
      
      // Clear state
      setIsConnected(false)
      setConnectedProvider(null)
      setAccessToken(null)
      setRefreshToken(null)
      setUserInfo(null)
      
      // Clear stored tokens
      cloudTokenService.clearAllTokens()
      
      console.log('✅ CloudStorageContext: Disconnected from cloud provider')
      return { success: true }
    } catch (error) {
      console.error('❌ CloudStorageContext: Disconnect error:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Test Google Drive connection
   */
  const testGoogleDriveConnection = async () => {
    return await cloudConnectionService.testGoogleDriveConnection()
  }

  /**
   * Clear cloud tokens
   */
  const clearCloudTokens = () => {
    cloudTokenService.clearAllTokens()
    setIsConnected(false)
    setConnectedProvider(null)
    setAccessToken(null)
    setRefreshToken(null)
    setUserInfo(null)
  }

  /**
   * Setup reconnection timer
   */
  const setupReconnectionTimer = () => {
    cloudReconnectionService.setupReconnectionTimer()
  }

  /**
   * Schedule reconnect attempt
   */
  const scheduleReconnectAttempt = () => {
    cloudReconnectionService.scheduleReconnectAttempt()
  }

  /**
   * Refresh access token
   */
  const refreshAccessToken = async () => {
    try {
      console.log('🔄 CloudStorageContext: Refreshing access token...')
      const newAccessToken = await cloudTokenService.refreshAccessToken()
      
      if (newAccessToken) {
        setAccessToken(newAccessToken)
        console.log('✅ CloudStorageContext: Access token refreshed successfully')
        return newAccessToken
      } else {
        console.log('❌ CloudStorageContext: Failed to refresh access token')
        return null
      }
    } catch (error) {
      console.error('❌ CloudStorageContext: Error refreshing access token:', error)
      return null
    }
  }

  /**
   * Reset reconnection state
   */
  const resetReconnectionState = () => {
    cloudReconnectionService.resetReconnectionState()
  }

  /**
   * Stop reconnection attempts
   */
  const stopReconnectionAttempts = () => {
    cloudReconnectionService.stopReconnectionAttempts()
  }

  /**
   * Upload file to cloud
   */
  const uploadFile = async (file, folderId = null) => {
    try {
      if (!isConnected) {
        throw new Error('Not connected to cloud storage')
      }

      if (connectedProvider === 'google_drive') {
        return await cloudFileService.uploadFile(file, folderId)
      }

      throw new Error(`Upload not supported for provider: ${connectedProvider}`)
    } catch (error) {
      console.error('❌ CloudStorageContext: Upload error:', error)
      throw error
    }
  }

  /**
   * Download file from cloud
   */
  const downloadFile = async (fileId) => {
    try {
      if (!isConnected) {
        throw new Error('Not connected to cloud storage')
      }

      if (connectedProvider === 'google_drive') {
        return await cloudFileService.downloadFile(fileId)
      }

      throw new Error(`Download not supported for provider: ${connectedProvider}`)
    } catch (error) {
      console.error('❌ CloudStorageContext: Download error:', error)
      throw error
    }
  }

  /**
   * Delete file from cloud
   */
  const deleteFile = async (fileId) => {
    try {
      if (!isConnected) {
        throw new Error('Not connected to cloud storage')
      }

      if (connectedProvider === 'google_drive') {
        return await cloudFileService.deleteFile(fileId)
      }

      throw new Error(`Delete not supported for provider: ${connectedProvider}`)
    } catch (error) {
      console.error('❌ CloudStorageContext: Delete error:', error)
      throw error
    }
  }

  /**
   * List files in cloud folder
   */
  const listFiles = async (folderId = null) => {
    try {
      if (!isConnected) {
        throw new Error('Not connected to cloud storage')
      }

      if (connectedProvider === 'google_drive') {
        return await cloudFileService.listFiles(folderId)
      }

      throw new Error(`List files not supported for provider: ${connectedProvider}`)
    } catch (error) {
      console.error('❌ CloudStorageContext: List files error:', error)
      throw error
    }
  }

  /**
   * Create folder in cloud
   */
  const createFolder = async (folderName, parentFolderId = null) => {
    try {
      if (!isConnected) {
        throw new Error('Not connected to cloud storage')
      }

      if (connectedProvider === 'google_drive') {
        return await cloudFileService.createFolder(folderName, parentFolderId)
      }

      throw new Error(`Create folder not supported for provider: ${connectedProvider}`)
    } catch (error) {
      console.error('❌ CloudStorageContext: Create folder error:', error)
      throw error
    }
  }

  /**
   * Get cloud storage settings
   */
  const getCloudStorageSettings = async () => {
    try {
      if (connectedProvider === 'google_drive') {
        return await cloudServerOperationsService.getCloudStorageSettings()
      }

      throw new Error(`Settings not supported for provider: ${connectedProvider}`)
    } catch (error) {
      console.error('❌ CloudStorageContext: Get settings error:', error)
      throw error
    }
  }

  /**
   * Update cloud storage settings
   */
  const updateCloudStorageSettings = async (settings) => {
    try {
      if (connectedProvider === 'google_drive') {
        return await cloudServerOperationsService.updateCloudStorageSettings(settings)
      }

      throw new Error(`Update settings not supported for provider: ${connectedProvider}`)
    } catch (error) {
      console.error('❌ CloudStorageContext: Update settings error:', error)
      throw error
    }
  }

  /**
   * Get storage quota from cloud provider
   */
  const getStorageQuota = async () => {
    try {
      if (!isConnected) {
        return {
          success: false,
          error: 'Not connected to cloud storage'
        }
      }

      if (connectedProvider === 'google_drive') {
        return await cloudFileService.getStorageQuota(isConnected, accessToken)
      }

      return {
        success: false,
        error: `Storage quota not supported for provider: ${connectedProvider}`
      }
    } catch (error) {
      console.error('❌ CloudStorageContext: Get storage quota error:', error)
      return {
        success: false,
        error: error.message || 'Failed to get storage quota'
      }
    }
  }

  const value = {
    // State
    isConnected,
    connectedProvider,
    accessToken,
    refreshToken,
    userInfo,
    loading,
    
    // Methods
    authenticateWithGoogleDrive,
    disconnectProvider,
    testGoogleDriveConnection,
    clearCloudTokens,
    refreshAccessToken,
    resetReconnectionState,
    stopReconnectionAttempts,
    
    // File operations
    uploadFile,
    downloadFile,
    deleteFile,
    listFiles,
    createFolder,
    
    // Settings
    getCloudStorageSettings,
    updateCloudStorageSettings,
    
    // Storage quota
    getStorageQuota
  }

  return (
    <CloudStorageContext.Provider value={value}>
      {children}
    </CloudStorageContext.Provider>
  )
}

export default CloudStorageProvider